import { useEffect, useState, useCallback } from 'react'
import { useCompanyGrowthStore } from '../store/companyGrowthStore'

/**
 * AI Monitoring Hook for Company Growth Analysis
 * Analyzes company metrics and generates intelligent insights
 */
export const useAIMonitoring = (autoAnalyze = true, intervalMs = 300000) => {
  const [insights, setInsights] = useState([])
  const [recommendations, setRecommendations] = useState([])
  const [alerts, setAlerts] = useState([])
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [lastAnalysis, setLastAnalysis] = useState(null)

  const store = useCompanyGrowthStore()
  const analyticsData = store.getFullAnalyticsData()
  const growthMetrics = store.getGrowthMetrics()

  /**
   * Analyzes company data and generates insights
   */
  const analyzeCompanyData = useCallback(() => {
    setIsAnalyzing(true)

    // Simulate AI analysis delay
    setTimeout(() => {
      const newInsights = generateInsights(analyticsData, growthMetrics)
      const newRecommendations = generateRecommendations(analyticsData, growthMetrics)
      const newAlerts = generateAlerts(analyticsData, growthMetrics)

      setInsights(newInsights)
      setRecommendations(newRecommendations)
      setAlerts(newAlerts)
      setLastAnalysis(new Date())

      // Update store with new insights
      const insightTexts = newInsights.map(i => i.text)
      store.updateAIInsights(insightTexts)

      setIsAnalyzing(false)
    }, 1500)
  }, [analyticsData, growthMetrics, store])

  // Auto-analysis on mount and interval
  useEffect(() => {
    if (autoAnalyze) {
      analyzeCompanyData()
      const interval = setInterval(analyzeCompanyData, intervalMs)
      return () => clearInterval(interval)
    }
  }, [autoAnalyze, intervalMs, analyzeCompanyData])

  return {
    insights,
    recommendations,
    alerts,
    isAnalyzing,
    lastAnalysis,
    analyzeCompanyData,
  }
}

/**
 * Generate AI insights from company data
 */
function generateInsights(data, metrics) {
  const insights = []

  // Growth trajectory insight
  const revenueGrowth = parseFloat(metrics.revenueGrowth)
  if (revenueGrowth > 20) {
    insights.push({
      type: 'positive',
      text: `🚀 Exceptional growth: ${revenueGrowth.toFixed(1)}% MoM revenue increase. Maintain momentum and consider scaling operations.`,
      priority: 'high',
    })
  } else if (revenueGrowth > 10) {
    insights.push({
      type: 'positive',
      text: `📈 Strong growth trajectory: ${revenueGrowth.toFixed(1)}% MoM with ${metrics.customerGrowth}% customer growth.`,
      priority: 'medium',
    })
  } else if (revenueGrowth < 0) {
    insights.push({
      type: 'warning',
      text: `⚠️ Revenue decline: ${revenueGrowth.toFixed(1)}% MoM. Investigate causes and adjust strategy.`,
      priority: 'high',
    })
  }

  // Customer retention insight
  const retention = data.metrics.customers.retention
  if (retention > 98) {
    insights.push({
      type: 'positive',
      text: `✨ Enterprise-grade retention: ${retention}% customer retention indicates strong product-market fit.`,
      priority: 'medium',
    })
  } else if (retention < 90) {
    insights.push({
      type: 'warning',
      text: `📉 Retention below target: ${retention}% churn rate. Focus on customer success and NPS improvement.`,
      priority: 'high',
    })
  }

  // Product performance insight
  const topProduct = data.products.reduce((a, b) => (a.growth > b.growth ? a : b), data.products[0])
  if (topProduct) {
    insights.push({
      type: 'neutral',
      text: `⭐ Top performer: ${topProduct.name} (${topProduct.growth}% growth, $${topProduct.revenue.toLocaleString()} revenue). Allocate more resources to scale.`,
      priority: 'medium',
    })
  }

  // Runway insight
  const burnRate = data.metrics.funding.burn_rate
  const runway = data.metrics.funding.runway_months
  if (runway < 12) {
    insights.push({
      type: 'warning',
      text: `⏱️ Runway alert: ${runway} months at $${(burnRate / 1000).toFixed(0)}K/mo burn rate. Accelerate fundraising.`,
      priority: 'critical',
    })
  } else if (runway < 18) {
    insights.push({
      type: 'neutral',
      text: `💰 Funding timeline: ${runway} months runway. Plan Series ${data.metrics.funding.next_round} in next 6 months.`,
      priority: 'medium',
    })
  }

  // Market opportunity insight
  const marketShare = (data.metrics.market.marketShare * 100).toFixed(3)
  if (data.metrics.market.targetableMarket > 100000) {
    insights.push({
      type: 'positive',
      text: `🎯 Market opportunity: ${marketShare}% share of ${data.metrics.market.targetableMarket.toLocaleString()} TAM. Scale GTM.`,
      priority: 'high',
    })
  }

  // Team composition insight
  const engineeringRatio = (data.metrics.team.engineering / data.metrics.team.total * 100).toFixed(0)
  if (engineeringRatio > 30) {
    insights.push({
      type: 'neutral',
      text: `👥 Engineering-heavy org: ${engineeringRatio}% engineers. Consider hiring sales/ops for growth.`,
      priority: 'low',
    })
  }

  return insights
}

/**
 * Generate actionable recommendations
 */
function generateRecommendations(data, metrics) {
  const recommendations = []

  // Revenue optimization
  const ltv = data.metrics.customers.ltv
  const cac = data.metrics.customers.cac
  const ltvCac = ltv / cac

  if (ltvCac > 3) {
    recommendations.push({
      action: 'Increase Customer Acquisition',
      rationale: `LTV:CAC ratio of ${ltvCac.toFixed(1)} is healthy. Consider increasing marketing spend by 20-30%.`,
      impact: 'high',
      timeline: 'Immediate',
    })
  } else if (ltvCac < 2) {
    recommendations.push({
      action: 'Reduce Customer Acquisition Cost',
      rationale: `LTV:CAC ratio of ${ltvCac.toFixed(1)} suggests CAC is too high. Optimize channels or targeting.`,
      impact: 'high',
      timeline: 'Immediate',
    })
  }

  // Product diversification
  if (data.products.length < 3) {
    recommendations.push({
      action: 'Expand Product Portfolio',
      rationale: 'With only 2 main products, revenue is concentrated. Develop adjacent products for stability.',
      impact: 'medium',
      timeline: '3-6 months',
    })
  }

  // Hiring strategy
  const hiringPlan = data.metrics.team.hiring_plan
  if (hiringPlan > 0) {
    recommendations.push({
      action: 'Execute Hiring Plan',
      rationale: `Plan to hire ${hiringPlan} people. Prioritize sales (to ${(data.metrics.team.total + hiringPlan) * 0.25}% of team) and ops roles.`,
      impact: 'high',
      timeline: 'Next 6 months',
    })
  }

  // Churn management
  const avgChurn = (data.monthlyData.reduce((sum, m) => sum + m.churn, 0) / data.monthlyData.length).toFixed(1)
  if (avgChurn > 2) {
    recommendations.push({
      action: 'Address High Churn',
      rationale: `Average monthly churn of ${avgChurn} is elevated. Launch retention program and improve NPS.`,
      impact: 'high',
      timeline: 'Immediate',
    })
  }

  // Opportunity prioritization
  const highLikelihoodOpps = data.opportunities.filter(o => o.likelihood === 'High' || o.likelihood === 'Medium-High')
  if (highLikelihoodOpps.length > 0) {
    recommendations.push({
      action: 'Prioritize Growth Opportunities',
      rationale: `${highLikelihoodOpps.length} high-likelihood opportunities available. Focus on: ${highLikelihoodOpps.map(o => o.title).join(', ')}`,
      impact: 'high',
      timeline: 'Next quarter',
    })
  }

  return recommendations
}

/**
 * Generate critical alerts
 */
function generateAlerts(data, metrics) {
  const alerts = []

  // Critical risk monitoring
  const criticalRisks = data.risks.filter(r => r.impact === 'High' || r.impact === 'Critical')
  criticalRisks.forEach(risk => {
    alerts.push({
      severity: 'critical',
      message: `⚠️ Critical Risk: ${risk.risk}`,
      details: `Mitigation: ${risk.mitigation}`,
      timestamp: new Date(),
    })
  })

  // Negative growth alert
  if (parseFloat(metrics.revenueGrowth) < 0) {
    alerts.push({
      severity: 'high',
      message: '🔴 Revenue Decline Detected',
      details: `${metrics.revenueGrowth}% MoM decline. Requires immediate investigation.`,
      timestamp: new Date(),
    })
  }

  // Runway alert
  if (data.metrics.funding.runway_months < 9) {
    alerts.push({
      severity: 'critical',
      message: '⏱️ Runway Critical',
      details: `Only ${data.metrics.funding.runway_months} months of runway remaining. Accelerate fundraising immediately.`,
      timestamp: new Date(),
    })
  }

  // High churn alert
  const latestMonth = data.monthlyData[data.monthlyData.length - 1]
  if (latestMonth && latestMonth.churn > 5) {
    alerts.push({
      severity: 'high',
      message: '📉 Elevated Customer Churn',
      details: `${latestMonth.churn} customers churned in ${latestMonth.month}. Launch retention campaign.`,
      timestamp: new Date(),
    })
  }

  return alerts
}
